import { useState } from 'react';
import { Question } from '@/types/questions';
import { Button } from '@/components/ui/button';
import React from 'react';
import type { UserData } from "@/lib/firebase/user";

interface ExamModeProps {
  questions: Question[];
  currentQuestionIndex: number;
  onSubmitExam: (answers: (string | null)[]) => void;
  onNextQuestion: () => void;
  onPreviousQuestion: () => void;
  totalQuestions: number;
  onAnswerSubmit: (answer: string) => void;
  userData: UserData | null;
  onShowPaywall: () => void;
  hasHitPaywall: boolean;
  onComplete: () => void;
}

const ExamMode = ({
  questions,
  currentQuestionIndex,
  onSubmitExam,
  onNextQuestion,
  onPreviousQuestion,
  totalQuestions,
  onAnswerSubmit,
  userData: _userData,
  onShowPaywall,
  hasHitPaywall,
  onComplete
}: ExamModeProps) => {
  const [answers, setAnswers] = useState<Record<number, string>>({});
  
  // Memoize current question
  const currentQuestion = React.useMemo(() => 
    questions[currentQuestionIndex], 
    [questions, currentQuestionIndex]
  );

  const isFirstQuestion = currentQuestionIndex === 0;

  const handleOptionSelect = (optionIndex: number) => {
    const selectedOption = String.fromCharCode(65 + optionIndex);
    
    // Call parent's onAnswerSubmit first to check if we can proceed
    onAnswerSubmit(selectedOption);

    // Only check paywall for non-premium users
    if (hasHitPaywall && !_userData?.isPremium) {
      onShowPaywall();
      return;
    }

    setAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: selectedOption
    }));
  };

  const handleNextQuestion = () => {
    // Only check paywall for non-premium users
    if (hasHitPaywall && !_userData?.isPremium) {
      onShowPaywall();
      return;
    }
    onNextQuestion();
  };

  const handleSubmitExam = () => {
    // Convert answers object to array format expected by onSubmitExam
    const answersArray = new Array(questions.length).fill(null);
    Object.entries(answers).forEach(([index, answer]) => {
      answersArray[parseInt(index)] = answer;
    });
    
    onSubmitExam(answersArray);
    onComplete();
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-4 sm:space-y-8 px-3 sm:px-0">
      <div className="bg-white p-4 sm:p-8 rounded-xl sm:rounded-3xl border border-gray-200 shadow-sm">
        <div className="space-y-3 sm:space-y-6">
          {/* Question Progress */}
          <div className="flex justify-between items-center text-xs sm:text-sm text-gray-500 font-medium">
            <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
          </div>

          {/* Question Content */}
          <div className="space-y-3 sm:space-y-4">
            {currentQuestion.imageUrl && (
              <div className="relative w-full bg-gray-50 rounded-lg flex justify-center">
                <img 
                  src={currentQuestion.imageUrl} 
                  alt="Question illustration"
                  className="max-w-full w-auto max-h-[70vh] object-contain p-4"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            <p className="text-sm sm:text-lg font-medium leading-relaxed">
              {currentQuestion.question}
            </p>
          </div>

          {/* Options */}
          <div className="space-y-2 sm:space-y-3">
            {currentQuestion.options.map((option, index) => {
              const optionLetter = String.fromCharCode(65 + index);
              return (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(index)}
                  className={`w-full min-h-[48px] p-3 sm:p-4 rounded-lg border text-left
                    text-sm sm:text-base transition-colors active:scale-[0.99]
                    ${answers[currentQuestionIndex] === optionLetter
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between gap-3">
        <Button
          onClick={onPreviousQuestion}
          disabled={isFirstQuestion}
          variant="outline"
          className="min-h-[44px] px-4 sm:px-6 text-sm sm:text-base flex-1 sm:flex-none"
        >
          Previous
        </Button>
        {currentQuestionIndex === questions.length - 1 ? (
          <Button
            onClick={handleSubmitExam}
            disabled={!answers[currentQuestionIndex]}
            className="min-h-[44px] px-4 sm:px-6 text-sm sm:text-base flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
          >
            Submit Exam
          </Button>
        ) : (
          <Button 
            onClick={handleNextQuestion}
            disabled={!answers[currentQuestionIndex]}
            className="min-h-[44px] px-4 sm:px-6 text-sm sm:text-base flex-1 sm:flex-none"
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
};

export default React.memo(ExamMode); 